import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { auth, db, createStripeBillingPortalSession, createStripeCheckoutSession, stopVippsAgreementSession, deleteMyAccount, endTesterProfile, logoutUser, updateUserProfile, storage, updateUserSettings, getPaymentHistory, fetchUserCreatedSpotsImpact } from '../services/firebaseService';
import { requestNotificationPermission, disableNotifications, getNotificationPermissionState } from '../services/notificationService';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { compressImage } from '../utils/imageCompression';
import { X, User, Camera, Loader2, Save, CheckCircle2, Zap, Car, Trash2, AlertTriangle, Route, Wrench, Home, Send, Smartphone, ExternalLink, Info, ChevronDown, Monitor, MessageSquare, ChevronLeft, Mail, MailOpen, ChevronRight, Bell, Award, MapPin, Truck, Lock, Check, Receipt, Sparkles, Moon, Sun, Layers, Tablet } from 'lucide-react';
import { enableMiniCards, isMiniCardsEnabled, getMiniCardsPlatformPrefs, setMiniCardsPlatformPref, detectMiniCardPlatform } from './map/MapMiniCards';
import { Capacitor } from '@capacitor/core';
import { GeofenceCheckIn } from '../src/plugins/geofence';
import { GEOFENCE_CHECKIN_ENABLED, GEOFENCE_OPTOUT_KEY } from '../constants';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../services/localeHelpers';
import { AppUser, SpotVisit, CollaborativeMap, CampingSpot } from '../types';
import TesterInboxModal from './TesterInboxModal';
import OffboardingSurveyModal from './OffboardingSurveyModal';
import VisitsTab from './profile/VisitsTab';
import TesterStatusTab from './profile/TesterStatusTab';
import BetaCountriesPanel from './profile/BetaCountriesPanel';
import GarageTab from './profile/GarageTab';
import { subscribeToVisits, getUserTier, getNextTier } from '../services/visitService';
import { computeTierBarPct, isNearTierGoal } from '../utils/tierProgress';
import { computeUserImpact } from '../utils/userImpact';
import { computeSetupChecklist, SetupStepKey } from '../utils/setupChecklist';
import { computeCheckinStreak } from '../utils/checkinStreak';
import { subscribeToUserMessages, subscribeToUserInboxMeta, markMessageAsRead } from '../services/userMessageService';
import { isAdminUser, hasPremiumAccess, REFERRAL_GA } from '../services/accessControl';
import { fetchMyReferralLink } from '../services/referralService';
import { useTheme } from '../contexts/ThemeContext';
import { NORWEGIAN_COUNTIES } from '../constants';
import { UserMessage, UserInboxMeta } from '../types';
import UserAvatar from './common/UserAvatar';
import TierBadge from './gamification/TierBadge';
import { USER_TIERS } from '../constants';
import { colorClasses } from '../utils/tailwindColorClasses';

interface ProfileOverlayProps {
  user: AppUser;
  onClose: () => void;
  onOpenUpgrade: () => void;
  collaborativeMaps?: CollaborativeMap[];
  isPaidUser?: boolean;
  spots?: CampingSpot[];
  onOpenCollabMap?: (mapId: string | null) => void;
  onFlyToSpot?: (spotId: string, lat: number, lng: number) => void;
  testerApplicationsEnabled?: boolean;
  onOpenTesterApplication?: () => void;
  onOpenInstallApp?: () => void;
}

const ProfileOverlay: React.FC<ProfileOverlayProps> = ({
  user, onClose, onOpenUpgrade,
  collaborativeMaps = [], isPaidUser = false, spots = [], onOpenCollabMap, onFlyToSpot,
  testerApplicationsEnabled = false, onOpenTesterApplication,
  onOpenInstallApp,
}) => {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const trapRef = useFocusTrap<HTMLDivElement>();
  const isAdmin = isAdminUser(user);
  const [activeTab, setActiveTab] = useState<'profile' | 'membership' | 'ambassador' | 'visits' | 'garage' | 'notifications'>('profile');
  const [profileSubTab, setProfileSubTab] = useState<'overview' | 'settings'>('overview');
  const [miniCardsPrefs, setMiniCardsPrefs] = useState(() => getMiniCardsPlatformPrefs());
  const [isMiniCardsSectionOpen, setIsMiniCardsSectionOpen] = useState(false);
  const currentPlatform = detectMiniCardPlatform();
  const [visits, setVisits] = useState<SpotVisit[]>([]);
  const [createdSpotsImpact, setCreatedSpotsImpact] = useState<Array<{ visitCount?: number; isVerified?: boolean; county?: string }>>([]);
  const [referralLink, setReferralLink] = useState<{ code: string; link: string } | null>(null);
  const [referralBusy, setReferralBusy] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const [inboxMessages, setInboxMessages] = useState<UserMessage[]>([]);
  const [inboxMeta, setInboxMeta] = useState<UserInboxMeta | null>(null);
  const [openMessageId, setOpenMessageId] = useState<string | null>(null);
  const [notifState, setNotifState] = useState(getNotificationPermissionState());

  useEffect(() => {
    if (!user?.uid) return;
    return subscribeToVisits(user.uid, setVisits);
  }, [user?.uid]);

  // «Din påvirkning» (#4B): les brukerens egne plasser klientside for impact-tall.
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    fetchUserCreatedSpotsImpact(user.uid)
      .then(spots => { if (!cancelled) setCreatedSpotsImpact(spots); })
      .catch(() => { /* impact er nice-to-have — feil skjules stille */ });
    return () => { cancelled = true; };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const unsubMsg = subscribeToUserMessages(user.uid, setInboxMessages);
    const unsubMeta = subscribeToUserInboxMeta(user.uid, setInboxMeta);
    return () => { unsubMsg(); unsubMeta(); };
  }, [user?.uid]);


  const [displayName, setDisplayName] = useState(user.displayName || '');
  const [vehicleType, setVehicleType] = useState(user.vehicleType);
  const detectInitialLang = (): 'en' | 'no' | 'de' | 'pl' | 'es' | 'fr' | 'nl' | 'sv' => {
    const l = i18n.language || '';
    if (/^(no|nb|nn)/.test(l)) return 'no';
    if (/^de/.test(l)) return 'de';
    if (/^pl/.test(l)) return 'pl';
    if (/^es/.test(l)) return 'es';
    if (/^fr/.test(l)) return 'fr';
    if (/^nl/.test(l)) return 'nl';
    if (/^sv/.test(l)) return 'sv';
    return 'en';
  };
  const [language, setLanguage] = useState<'en' | 'no' | 'de' | 'pl' | 'es' | 'fr' | 'nl' | 'sv'>(user.language || detectInitialLang());
  const [offroadRoadFilters, setOffroadRoadFilters] = useState(user.offroadRoadFilters || {
    track: false,
    unclassified: false,
    service: false,
    residential: false
  });
  // needsEmptying is now derived from active vehicle's grey_water_tank/fixed_toilet equipment
  const [countyNotifSpots, setCountyNotifSpots] = useState<string[]>(user.county_notifications_spots || []);
  const [countyNotifReviews, setCountyNotifReviews] = useState<string[]>(user.county_notifications_reviews || []);
  const [isUpdating, setIsUpdating] = useState(false);
  const [countySaveStatus, setCountySaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const countySaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveCountyPrefs = useCallback(async (nextSpots: string[], nextReviews: string[]) => {
    setCountySaveStatus('saving');
    if (countySaveTimer.current) clearTimeout(countySaveTimer.current);
    try {
      await updateUserSettings(user.uid, {
        county_notifications_spots: nextSpots,
        county_notifications_reviews: nextReviews,
      });
      setCountySaveStatus('saved');
      countySaveTimer.current = setTimeout(() => setCountySaveStatus('idle'), 1500);
    } catch (err) {
      console.error('Failed to save county preferences:', err);
      setCountySaveStatus('error');
      countySaveTimer.current = setTimeout(() => setCountySaveStatus('idle'), 3000);
    }
  }, [user.uid]);


  const [isOpeningBilling, setIsOpeningBilling] = useState(false);
  const [isStartingSubscription] = useState(false);
  const [isEndingTester, setIsEndingTester] = useState(false);
  const [confirmStopVipps, setConfirmStopVipps] = useState(false);
  const [confirmEndTester, setConfirmEndTester] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<Array<{ id: string; date: number; amount: number; currency: string; status: string; description: string; provider: 'stripe' | 'vipps'; receiptUrl: string | null }>>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Offboarding survey shown before account deletion / subscription cancellation
  const [offboarding, setOffboarding] = useState<{
    type: 'cancellation' | 'deletion';
    provider?: string;
    onContinue: () => void;
  } | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [showTesterInbox, setShowTesterInbox] = useState(false);
  const [notifPending, setNotifPending] = useState(false);
  const [optimisticNotifEnabled, setOptimisticNotifEnabled] = useState<boolean | null>(null);

  const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

  const effectiveNotifEnabled = optimisticNotifEnabled ?? !!user.notificationsEnabled;

  // Bakgrunns-innsjekk (native OS-geofence). Vises kun når native-pluginen finnes
  // i denne bygg-en (dvs. etter Trinn 2 butikk-release). På/av speiler «Tillat
  // hele tiden»-tillatelsen kombinert med lokal opt-out.
  const [geofenceAvailable, setGeofenceAvailable] = useState(false);
  const [geofenceOn, setGeofenceOn] = useState(false);
  const [geofencePending, setGeofencePending] = useState(false);
  useEffect(() => {
    if (!(Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('GeofenceCheckIn'))) return;
    setGeofenceAvailable(true);
    GeofenceCheckIn.checkPermissions()
      .then(p => {
        let optedOut = false;
        try { optedOut = localStorage.getItem(GEOFENCE_OPTOUT_KEY) === '1'; } catch { /* ignore */ }
        setGeofenceOn(p.granted && !optedOut);
      })
      .catch(() => { /* plugin utilgjengelig */ });
  }, []);

  // Clear optimistic state once Firestore has propagated the real value back.
  useEffect(() => {
    if (optimisticNotifEnabled !== null) {
      setOptimisticNotifEnabled(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.notificationsEnabled]);

  useEffect(() => {
    setLanguage(detectInitialLang());
  }, [i18n.language]);

  const handleLanguageSelect = (nextLanguage: 'en' | 'no' | 'de' | 'pl' | 'es' | 'fr' | 'nl' | 'sv') => {
    setLanguage(nextLanguage);
    i18n.changeLanguage(nextLanguage);
  };

  const handleThemeSelect = (nextTheme: 'dark' | 'light') => {
    setTheme(nextTheme);
    if (user?.uid) {
      updateUserSettings(user.uid, { themePreference: nextTheme });
    }
  };

  const handleUpdateProfile = async () => {
    setIsUpdating(true);
    try {
      if (auth.currentUser) {
        await updateUserProfile(auth.currentUser, { displayName });
        await updateUserSettings(auth.currentUser.uid, {
          // Only save legacy vehicle settings if user has no garage vehicle
          ...(user.activeVehicleId ? {} : { vehicleType, offroadRoadFilters }),
          language,
          county_notifications_spots: countyNotifSpots,
          county_notifications_reviews: countyNotifReviews,
        });
        i18n.changeLanguage(language);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    setIsUploadingPhoto(true);
    try {
      const { file: compressed } = await compressImage(file);
      const storageRef = ref(storage, `profile_photos/${auth.currentUser.uid}`);
      await uploadBytes(storageRef, compressed);
      const photoURL = await getDownloadURL(storageRef);
      await updateUserProfile(auth.currentUser, { photoURL });
      // Also persist to Firestore so it takes priority over Google's default photo
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { photoURL });
    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const doManageBilling = async () => {
    setIsOpeningBilling(true);
    try {
      const { url } = await createStripeBillingPortalSession();
      window.location.href = url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
    } finally {
      setIsOpeningBilling(false);
    }
  };
  // Ask why before sending the user to the cancellation portal
  const handleManageBilling = () => {
    setOffboarding({
      type: 'cancellation',
      provider: user.subscriptionProvider || 'stripe',
      onContinue: () => { setOffboarding(null); doManageBilling(); },
    });
  };

  const [isStoppingVipps, setIsStoppingVipps] = useState(false);
  const doStopVipps = async () => {
    setIsStoppingVipps(true);
    try {
      await stopVippsAgreementSession();
    } catch (error) {
      console.error('Error stopping Vipps agreement:', error);
    } finally {
      setIsStoppingVipps(false);
    }
  };
  // Ask why before actually stopping the Vipps agreement
  const handleStopVipps = () => {
    setOffboarding({
      type: 'cancellation',
      provider: 'vipps',
      onContinue: () => { setOffboarding(null); doStopVipps(); },
    });
  };

  // "Start Supporter" opens the UpgradeOverlay which offers both Stripe and Vipps
  const handleStartSubscription = () => {
    onOpenUpgrade();
  };

  const handleViewPaymentHistory = async () => {
    setShowPaymentHistory(true);
    setIsLoadingHistory(true);
    try {
      const { transactions } = await getPaymentHistory();
      setPaymentHistory(transactions);
    } catch (error) {
      console.error('Error fetching payment history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleEndTester = async () => {
    if (!confirmEndTester) { setConfirmEndTester(true); return; }
    setConfirmEndTester(false);
    setIsEndingTester(true);
    try {
      await endTesterProfile();
      onClose();
    } catch (error) {
      console.error('Error ending tester profile:', error);
    } finally {
      setIsEndingTester(false);
    }
  };

  // Show the offboarding survey first, then the destructive confirm dialog
  const openDeleteFlow = () => {
    setOffboarding({
      type: 'deletion',
      provider: user.subscriptionProvider || undefined,
      onContinue: () => { setOffboarding(null); setDeleteError(null); setShowDeleteConfirm(true); },
    });
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteMyAccount();
      window.location.reload();
    } catch (error: any) {
      console.error('Error deleting account:', error);
      if (error?.code === 'auth/requires-recent-login') {
        setDeleteError(t('profile.errors.recent_login'));
      } else {
        setDeleteError(error?.message || t('profile.errors.deletion_failed'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-end md:items-center justify-center animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={onClose} />

      <div ref={trapRef} role="dialog" aria-modal="true" className="relative w-full max-w-lg h-[100dvh] md:h-auto bg-[var(--bg-base)] md:border border-[var(--border-secondary)] rounded-t-modal md:rounded-[3rem] shadow-3xl overflow-hidden flex flex-col max-h-full md:max-h-[90vh] transition-all" style={{ paddingTop: 'calc(var(--safe-area-top, 0px) + 1.5rem)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header - More compact */}
        <div className="p-4 md:p-6 md:pb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
              <User size={20} />
            </div>
            <div className="space-y-0.5">
              <h2 className="text-lg md:text-xl font-black text-[var(--text-primary)] tracking-tight leading-none">{t('profile.title')}</h2>
              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all">
            <X size={18} />
          </button>
        </div>

        <div className="relative border-b border-[var(--border-primary)] bg-[var(--bg-input)]">
          <div className="flex w-full py-0.5">
            {[
              { id: 'profile', icon: User, label: t('profile.tabs.profile'), color: 'emerald' },
              { id: 'membership', icon: Zap, label: t('profile.tabs.membership'), color: 'blue' },
              ...(user.isTester ? [{ id: 'ambassador', icon: Award, label: t('profile.tabs.ambassador', 'Ambassadør'), color: 'amber' }] : []),
              { id: 'garage', icon: Truck, label: t('profile.tabs.garage', 'Garasje'), color: 'orange' },
              { id: 'notifications', icon: Bell, label: t('profile.tabs.notifications', 'Varsler'), color: 'violet' },
              { id: 'visits', icon: CheckCircle2, label: (() => { const tt = getUserTier(visits, user?.verifiedSpotCount || 0); return t(`tierGuide.tierNames.${tt.level}`, tt.name); })(), color: 'emerald' }
            ].map((tab) => {
              const tc = colorClasses(tab.color);
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 text-3xs font-black uppercase tracking-tight transition-all relative whitespace-nowrap group ${isActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                >
                  <div className="relative">
                    <tab.icon size={14} className={isActive ? tc.text500 : 'text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]'} />
                    {tab.id === 'notifications' && !isPaidUser && (
                      <Lock size={7} className="absolute -top-1 -right-1.5 text-amber-500" />
                    )}
                    {tab.id === 'membership' && !isPaidUser && !user.isTester && !isAdmin && (
                      <span className="absolute -top-1 -right-1.5 w-2 h-2 bg-emerald-500 rounded-full ring-2 ring-[var(--bg-input)]" />
                    )}
                  </div>
                  <span className="hidden sm:inline-block leading-none">{tab.label}</span>
                  {isActive && (
                    <div className={`absolute bottom-0 left-1 right-1 h-0.5 ${tc.bg500} rounded-full shadow-[0_0_8px_rgba(16,185,129,0.5)]`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-4 md:space-y-6" style={{ overscrollBehavior: 'contain' }}>
          {activeTab === 'profile' && (() => {
            const vc = user?.verifiedSpotCount || 0;
            const currentTier = getUserTier(visits, vc);
            const nextTier = getNextTier(visits, vc);
            const uniqueSpots = new Set(visits.map(v => v.spotId)).size;
            const score = uniqueSpots + vc;
            const spotsToNextTier = nextTier ? Math.max(0, nextTier.threshold - score) : 0;
            // Endowed progress + nær-mål-emphasis, samme motor som reward-toasten (#2A/#2C).
            const tierBarPct = computeTierBarPct(spotsToNextTier, nextTier);
            const nearGoal = isNearTierGoal(spotsToNextTier, nextTier);
            // «Din påvirkning» (#4B Endowment): synliggjør akkumulert eierskap.
            const impact = computeUserImpact(createdSpotsImpact, visits);
            // «Kom i gang»-sjekkliste (#2B Goal Gradient): onboarding-gradient for nye brukere.
            const setup = computeSetupChecklist({
              hasVehicle: !!user.activeVehicleId,
              hasAvatar: !!user.photoURL,
              hasCheckin: visits.length > 0,
              hasContribution: createdSpotsImpact.length > 0,
              notificationsEnabled: !!user.notificationsEnabled,
            });
            // Positiv innsjekk-streak (#6C): skyldfri — vises kun når den lever (≥2 uker).
            const streak = computeCheckinStreak(visits.map(v => v.visitedAt), Date.now());
            const setupLabels: Record<SetupStepKey, string> = {
              vehicle: t('profile.setup.steps.vehicle', 'Legg til kjøretøy'),
              avatar: t('profile.setup.steps.avatar', 'Velg profilbilde'),
              checkin: t('profile.setup.steps.checkin', 'Første innsjekk'),
              contribution: t('profile.setup.steps.contribution', 'Legg til en plass'),
              notifications: t('profile.setup.steps.notifications', 'Skru på varsler'),
            };

            return (
              <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300 pb-12">
                {/* Profile Card */}
                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-3xl p-4 flex items-center gap-5">
                  <div className="relative flex-shrink-0">
                    <UserAvatar
                      photoURL={user.photoURL}
                      tier={currentTier.level}
                      size="xl"
                    />
                    <label className="absolute -bottom-1 -right-1 p-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-lg cursor-pointer transition-all active:scale-90 z-10 border border-black/50">
                      <Camera size={12} />
                      <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} disabled={isUploadingPhoto} />
                    </label>
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 ml-1">
                        <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('profile.display_name')}</label>
                        <TierBadge tier={currentTier.level} showName className="opacity-80 scale-90 origin-left" />
                      </div>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="w-full bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-4 py-2 text-[var(--text-primary)] text-[14px] font-bold focus:border-emerald-500/30 outline-none transition-all"
                        placeholder={t('profile.name_on_map_placeholder')}
                      />
                    </div>
                  </div>
                </div>

                {/* Sub-tab Switcher */}
                <div className="flex bg-[var(--bg-input)] p-1 rounded-2xl border border-[var(--border-primary)] mx-1">
                  <button
                    onClick={() => setProfileSubTab('overview')}
                    className={`flex-1 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${profileSubTab === 'overview' ? 'bg-emerald-600 text-white shadow-lg' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                  >
                    {t('profile.tabs.overview', 'Oversikt')}
                  </button>
                  <button
                    onClick={() => setProfileSubTab('settings')}
                    className={`flex-1 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${profileSubTab === 'settings' ? 'bg-emerald-600 text-white shadow-lg' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                  >
                    {t('profile.tabs.settings', 'Innstillinger')}
                  </button>
                </div>

                {profileSubTab === 'overview' ? (
                  <div className="space-y-4 animate-in fade-in duration-300">
                    {/* «Kom i gang»-sjekkliste (#2B Goal Gradient): vises til alt er gjort,
                        forsvinner så permanent — ingen mas på erfarne brukere (#5-budsjettet). */}
                    {!setup.allDone && (
                      <div className="bg-emerald-600/10 border border-emerald-500/20 rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-2xs font-black uppercase tracking-widest text-emerald-400">
                            {t('profile.setup.title', 'Kom i gang')}
                          </p>
                          <span className="text-2xs font-black text-emerald-300 tabular-nums">{setup.doneCount}/{setup.total}</span>
                        </div>
                        <div className="mb-3 h-1.5 bg-[var(--bg-input)] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                            style={{ width: `${(setup.doneCount / setup.total) * 100}%`, boxShadow: '0 0 8px rgba(16,185,129,0.4)' }}
                          />
                        </div>
                        <div className="space-y-1.5">
                          {setup.steps.map(step => {
                            const isNext = step.key === setup.nextKey;
                            return (
                              <div
                                key={step.key}
                                className={`flex items-center gap-2 text-tiny ${step.done ? 'text-[var(--text-muted)]' : isNext ? 'text-[var(--text-primary)] font-bold' : 'text-[var(--text-secondary)]'}`}
                              >
                                {step.done
                                  ? <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                                  : <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${isNext ? 'border-emerald-400' : 'border-[var(--border-primary)]'}`} />}
                                <span className={step.done ? 'line-through' : ''}>{setupLabels[step.key]}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* «Din reise» — samlet status (#5 tetthets-audit): tier + streak +
                        påvirkning i ÉN ramme med interne skiller, i stedet for tre
                        konkurrerende kort. Færre rammer = respekterer design-budsjettet. */}
                    <div className="bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] divide-y divide-[var(--border-primary)] overflow-hidden">
                      {/* Tier + progresjon (#2A/#2C) */}
                      <div className="p-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <TierBadge tier={currentTier.level} showName={false} />
                          <div className="flex-1">
                            <span className="text-sm font-black" style={{ color: currentTier.color }}>
                              {t(`tierGuide.tierNames.${currentTier.level}`, currentTier.name)}
                            </span>
                            <p className="text-2xs text-[var(--text-muted)]">{t('profile.unique_spots_visited', '{{count}} unike plasser besøkt', { count: uniqueSpots })}</p>
                          </div>
                        </div>
                        {nextTier && (
                          <div>
                            <div className="flex justify-between items-center mb-1.5">
                              <span className={`text-3xs font-black uppercase tracking-widest ${nearGoal ? 'text-amber-300' : 'text-[var(--text-muted)]'}`}>
                                {nearGoal
                                  ? `🔥 ${t('gamification.contribution.almost_there', { count: spotsToNextTier, tier: t(`tierGuide.tierNames.${nextTier.level}`, nextTier.name), defaultValue: 'Bare {{count}} igjen til {{tier}}!' })}`
                                  : t('profile.spots_to_next_tier', '{{count}} til {{tier}}', { count: spotsToNextTier, tier: t(`tierGuide.tierNames.${nextTier.level}`, nextTier.name) })}
                              </span>
                              <span className="text-3xs font-black text-[var(--text-muted)]">
                                {score} / {nextTier.threshold}
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-[var(--bg-input)] rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-1000 ease-out ${nearGoal ? 'animate-pulse' : ''}`}
                                style={{
                                  width: `${tierBarPct}%`,
                                  backgroundColor: nextTier.color,
                                  boxShadow: `0 0 ${nearGoal ? 14 : 8}px ${nextTier.color}${nearGoal ? '99' : '44'}`
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Positiv innsjekk-streak (#6C): rolig, skyldfri — inline rad uten egen
                          ramme, vises kun når rekka lever (≥2 uker). */}
                      {streak.weeks >= 2 && (
                        <div className="p-4 flex items-center gap-2.5">
                          <Award size={18} className="text-emerald-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-black text-[var(--text-primary)]">
                              {t('profile.streak.weeks', '{{count}} uker på rad', { count: streak.weeks })}
                            </p>
                            <p className="text-2xs text-[var(--text-muted)]">
                              {t('profile.streak.subtitle', 'Fin rekke — fortsett den gode flyten.')}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Din påvirkning (#4B Endowment): akkumulert eierskap. Skjules for nye
                          brukere (impact.hasAny). */}
                      {impact.hasAny && (
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles size={13} className="text-emerald-400" />
                            <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)]">
                              {t('profile.impact.title', 'Din påvirkning')}
                            </p>
                          </div>
                          {impact.spotsCreated > 0 && impact.spotsCreatedUseCount > 0 && (
                            <p className="text-tiny text-[var(--text-secondary)] mb-3 leading-relaxed">
                              {t('profile.impact.headline', 'Plassene du la til er brukt {{count}} ganger av reisende.', { count: impact.spotsCreatedUseCount })}
                            </p>
                          )}
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { show: true, value: impact.spotsVisited, label: t('profile.impact.visited', 'Besøkt') },
                              { show: impact.countiesVisited > 0, value: impact.countiesVisited, label: t('profile.impact.counties', 'Fylker') },
                              { show: impact.nights > 0, value: impact.nights, label: t('profile.impact.nights', 'Netter') },
                              { show: impact.spotsCreated > 0, value: impact.spotsCreated, label: t('profile.impact.created', 'Lagt til') },
                            ].filter(tile => tile.show).map((tile, i) => (
                              <div key={i} className="bg-[var(--bg-input)] rounded-xl p-2.5 text-center border border-[var(--border-primary)]">
                                <div className="text-lg font-black text-[var(--text-primary)] tabular-nums">{tile.value.toLocaleString('nb-NO')}</div>
                                <div className="text-3xs font-bold uppercase tracking-widest text-[var(--text-muted)]">{tile.label}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Verv en venn (#3 Reciprocity) — gated bak REFERRAL_GA (default av) til
                        svindel-modellen er gjennomgått + server-flagget skrus på. */}
                    {REFERRAL_GA && (
                      <div className="bg-emerald-600/10 border border-emerald-500/20 rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Send size={14} className="text-emerald-400" />
                          <p className="text-2xs font-black uppercase tracking-widest text-emerald-400">{t('referral.title', 'Verv en venn')}</p>
                        </div>
                        <p className="text-tiny text-[var(--text-secondary)] mb-3 leading-relaxed">
                          {t('referral.body', 'Del lenken din. Vennen får en gratis premium-måned med en gang — og du får din når de sjekker inn første gang.')}
                        </p>
                        {referralLink ? (
                          <div className="flex items-center gap-2">
                            <input readOnly value={referralLink.link} className="flex-1 min-w-0 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-3 py-2 text-2xs text-[var(--text-secondary)]" />
                            <button
                              onClick={() => { try { navigator.clipboard?.writeText(referralLink.link); } catch { /* ignore */ } setReferralCopied(true); setTimeout(() => setReferralCopied(false), 2000); }}
                              className="shrink-0 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 text-2xs font-black uppercase tracking-widest"
                            >
                              {referralCopied ? t('common.copied', 'Kopiert') : t('common.copy', 'Kopier')}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={async () => { setReferralBusy(true); try { setReferralLink(await fetchMyReferralLink()); } catch { /* ignore */ } finally { setReferralBusy(false); } }}
                            disabled={referralBusy}
                            className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-2xs font-black uppercase tracking-widest disabled:opacity-50"
                          >
                            {referralBusy ? t('common.loading', 'Laster …') : t('referral.get_link', 'Hent vervelenke')}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Route View Badges */}
                    {user.earned_route_badges && user.earned_route_badges.length > 0 && (
                      <div className="bg-[var(--bg-surface-alt)] rounded-2xl p-4 border border-[var(--border-primary)]">
                        <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-3">
                          {t('profile.route_badges')}
                        </p>
                        <div className="space-y-2">
                          {user.earned_route_badges.map((badge) => (
                            <div key={`${badge.routeId}-${badge.threshold}`} className="flex items-center gap-3 bg-[var(--bg-surface-alt)] p-3 rounded-xl border border-[var(--border-primary)]">
                              <span className="text-2xl">{badge.emoji}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-tight truncate">
                                  {badge.badgeName}
                                </div>
                                <div className="text-3xs text-[var(--text-muted)] truncate">
                                  {badge.routeName} — {badge.threshold} {t('route.views')}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Language */}
                    <div className="space-y-1">
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Smartphone size={16} className="text-[var(--text-muted)]" />
                          <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('profile.language')}</span>
                        </div>
                        <div className="flex flex-wrap bg-[var(--bg-input)] p-0.5 rounded-lg border border-[var(--border-primary)]">
                          {(['no', 'sv', 'en', 'de', 'pl', 'es', 'fr', 'nl'] as const).map(code => (
                            <button
                              key={code}
                              onClick={() => handleLanguageSelect(code)}
                              className={`px-2.5 py-1.5 rounded-md text-3xs font-black uppercase tracking-widest transition-all ${language === code ? 'bg-blue-600 text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                            >
                              {code}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Theme */}
                    <div className="space-y-1">
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {theme === 'dark' ? <Moon size={16} className="text-[var(--text-muted)]" /> : <Sun size={16} className="text-amber-400" />}
                          <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('profile.theme', 'Tema')}</span>
                        </div>
                        <div className="flex bg-[var(--bg-input)] p-0.5 rounded-lg border border-[var(--border-primary)]">
                          <button
                            onClick={() => handleThemeSelect('dark')}
                            className={`px-3 py-1.5 rounded-md text-3xs font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${theme === 'dark' ? 'bg-blue-600 text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                          >
                            <Moon size={10} /> {t('profile.theme_dark', 'Mork')}
                          </button>
                          <button
                            onClick={() => handleThemeSelect('light')}
                            className={`px-3 py-1.5 rounded-md text-3xs font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${theme === 'light' ? 'bg-blue-600 text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                          >
                            <Sun size={10} /> {t('profile.theme_light', 'Lys')}
                          </button>
                        </div>
                      </div>
                    </div>

	                    {/* Mini-cards per-platform toggles */}
	                    <div className="space-y-1">
	                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3">
	                        <button
	                          type="button"
	                          onClick={() => setIsMiniCardsSectionOpen(prev => !prev)}
	                          className="w-full flex items-center justify-between gap-3"
	                        >
	                          <div className="flex items-center gap-3 text-left">
	                            <Layers size={16} className="text-[var(--text-muted)]" />
	                            <div>
	                              <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest block">{t('profile.mini_cards_title', 'Plassoversikt')}</span>
	                              <span className="text-3xs text-[var(--text-muted)]">{t('profile.mini_cards_subtitle', 'Horisontale mini-kort på kartet')}</span>
	                            </div>
	                          </div>
	                          {isMiniCardsSectionOpen ? (
	                            <ChevronDown size={16} className="text-[var(--text-muted)]" />
	                          ) : (
	                            <ChevronRight size={16} className="text-[var(--text-muted)]" />
	                          )}
	                        </button>
	                        {isMiniCardsSectionOpen && (
	                          <div className="flex flex-col gap-2 mt-3">
	                            {([
	                              { key: 'mobile' as const, icon: <Smartphone size={13} />, label: 'Mobil' },
	                              { key: 'tablet' as const, icon: <Tablet size={13} />, label: 'Nettbrett' },
	                              { key: 'desktop' as const, icon: <Monitor size={13} />, label: 'Desktop' },
	                            ]).map(({ key, icon, label }) => (
	                              <div key={key} className="flex items-center justify-between pl-1">
	                                <div className="flex items-center gap-2">
	                                  <span className="text-[var(--text-muted)]">{icon}</span>
	                                  <span className="text-2xs font-bold text-[var(--text-secondary)]">
	                                    {label}
	                                    {key === currentPlatform && (
	                                      <span className="ml-1 text-4xs font-semibold text-emerald-500 uppercase">(denne)</span>
	                                    )}
	                                  </span>
	                                </div>
	                                <button
	                                  type="button"
	                                  onClick={() => {
	                                    const next = !miniCardsPrefs[key];
	                                    setMiniCardsPlatformPref(key, next);
	                                    setMiniCardsPrefs(prev => ({ ...prev, [key]: next }));
	                                  }}
	                                  className={`relative w-10 h-5 rounded-full transition-colors ${miniCardsPrefs[key] ? 'bg-emerald-500' : 'bg-[var(--bg-input)] border border-[var(--border-secondary)]'}`}
	                                >
	                                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${miniCardsPrefs[key] ? 'translate-x-5' : ''}`} />
	                                </button>
	                              </div>
	                            ))}
	                          </div>
	                        )}
	                      </div>
	                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 animate-in fade-in duration-300">
                    <div className="space-y-1">
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3">
                        <div className="flex items-center gap-3 mb-2">
                          <Car size={16} className="text-[var(--text-muted)]" />
                          <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('profile.vehicle_and_filter')}</span>
                        </div>

                        {/* Vehicle-derived terrain info or legacy toggle */}
                        {user.activeVehicleId ? (
                          <div className="space-y-2 animate-in fade-in duration-200">
                            {user.activeVehicleTerrainPrefs && user.activeVehicleTerrainPrefs.length > 0 ? (
                              <>
                                <p className="text-3xs text-[var(--text-muted)] font-bold">
                                  {t('profile.terrain_from_vehicle')}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {user.activeVehicleTerrainPrefs.map(tp => (
                                    <span
                                      key={tp}
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-4xs font-black uppercase tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/20"
                                    >
                                      {t(`profile.garage.terrain.${tp}`)}
                                    </span>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <p className="text-3xs text-[var(--text-muted)] font-bold">
                                {t('profile.filter_from_garage', 'Veityper styres av ditt kjøretøy i garasjen')}
                              </p>
                            )}
                            <button
                              onClick={() => setActiveTab('garage')}
                              className="text-3xs font-bold text-emerald-400 hover:text-emerald-300 transition-all"
                            >
                              {t('profile.change_in_garage')} →
                            </button>
                          </div>
                        ) : (
                          /* Legacy standard/offroad toggle — only for users without a garage vehicle */
                          <div className="space-y-2">
                            <div className="flex bg-[var(--bg-input)] p-0.5 rounded-lg border border-[var(--border-primary)] w-fit">
                              <button
                                onClick={() => setVehicleType('standard')}
                                className={`px-3 py-1.5 rounded-md text-3xs font-black uppercase tracking-widest transition-all ${vehicleType === 'standard' ? 'bg-emerald-600 text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                              >
                                {t('auth.standard')}
                              </button>
                              <button
                                onClick={() => setVehicleType('offroad')}
                                className={`px-3 py-1.5 rounded-md text-3xs font-black uppercase tracking-widest transition-all ${vehicleType === 'offroad' ? 'bg-emerald-600 text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                              >
                                {t('auth.offroad')}
                              </button>
                            </div>
                            <p className="text-4xs text-[var(--text-muted)]">
                              {t('profile.terrain_add_vehicle_hint')}
                            </p>
                          </div>
                        )}
                      </div>

                      {!user.activeVehicleId && vehicleType === 'offroad' && (
                        <div className="bg-emerald-500/5 border border-emerald-500/10 p-4 rounded-2xl space-y-3 animate-in fade-in slide-in-from-top-1">
                          <p className="text-3xs font-black text-emerald-500 uppercase tracking-widest text-center">{t('profile.show_road_types')}</p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { key: 'track', label: t('spot.tractor_road') },
                              { key: 'unclassified', label: t('profile.road_types.unclassified') },
                              { key: 'service', label: t('profile.road_types.service') },
                              { key: 'residential', label: t('profile.road_types.residential') }
                            ].map(filter => (
                              <button
                                key={filter.key}
                                onClick={() => setOffroadRoadFilters(prev => ({ ...prev, [filter.key]: !prev[filter.key as keyof typeof prev] }))}
                                className={`py-2 rounded-xl text-3xs font-black uppercase tracking-widest transition-all border ${offroadRoadFilters[filter.key as keyof typeof offroadRoadFilters] ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30' : 'bg-[var(--bg-input)] border-[var(--border-primary)] text-[var(--text-muted)]'}`}
                              >
                                {filter.label}
                              </button>
                            ))}
                          </div>
                          {offroadRoadFilters.track && (
                            <div className="flex items-start gap-2 px-1 animate-in fade-in slide-in-from-top-1 duration-200">
                              <AlertTriangle size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
                              <p className="text-3xs font-bold text-amber-400/80 leading-relaxed">{t('profile.road_types.track_warning')}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Install native apps */}
                      {!Capacitor.isNativePlatform() && onOpenInstallApp && (
                        <button
                          type="button"
                          onClick={onOpenInstallApp}
                          className="w-full mt-3 bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4 flex items-center gap-3 transition-all active:scale-[0.98]"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                            <Smartphone size={18} />
                          </div>
                          <div className="min-w-0 flex-1 text-left">
                            <p className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-widest">
                              {t('install_app.menu_label')}
                            </p>
                            <p className="text-2xs text-[var(--text-muted)] leading-relaxed">
                              {/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
                                ? 'App Store'
                                : /Android/.test(navigator.userAgent)
                                  ? 'Google Play'
                                  : 'Android, iOS & Desktop'}
                            </p>
                          </div>
                          <ChevronRight size={14} className="shrink-0 text-[var(--text-muted)]" />
                        </button>
                      )}

                      <div className="mt-4 pt-4 border-t border-[var(--border-primary)]">
                        <button
                          type="button"
                          onClick={openDeleteFlow}
                          className="w-full bg-red-600/5 hover:bg-red-600/10 border border-red-600/20 text-red-400 py-3 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95"
                        >
                          <Trash2 size={14} /> {t('profile.account_actions.delete_account')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <button
                  onClick={handleUpdateProfile}
                  disabled={isUpdating}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3.5 rounded-2xl font-black text-2xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 shadow-xl shadow-emerald-900/10"
                >
                  {isUpdating ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                  {t('profile.save_changes')}
                </button>
              </div>
            );
          })()}

          {activeTab === 'membership' && (
            <div className="space-y-1 animate-in slide-in-from-bottom-2 duration-300 pb-8">
              <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Zap size={16} className="text-blue-400" />
                  <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('profile.membership_status.label')}</span>
                </div>
                {isAdmin || user.subscriptionStatus === 'paid' || user.isTester ? (
                  <div className="flex items-center gap-1.5">
                    <span className={`text-3xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md border ${isAdmin ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : user.isTester ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : hasPremiumAccess(user) ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                      {isAdmin ? t('account.admin') : user.isTester ? t('account.tester') : hasPremiumAccess(user) ? 'Premium' : t('account.supporter')}
                    </span>
                    {!isAdmin && !user.isTester && user.subscriptionStatus === 'paid' && (
                      <span className={`text-4xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${user.subscriptionProvider === 'apple' ? 'bg-stone-500/10 text-[var(--text-secondary)]' : user.subscriptionProvider === 'vipps' ? 'bg-vipps/10 text-vipps' : 'bg-indigo-500/10 text-indigo-400'}`}>
                        {user.subscriptionProvider === 'apple' ? t('profile.membership_status.provider_apple', 'Apple') : user.subscriptionProvider === 'vipps' ? t('profile.membership_status.provider_vipps') : t('profile.membership_status.provider_stripe')}
                      </span>
                    )}
                  </div>
                ) : user.paidUntil && Date.now() < user.paidUntil ? (
                  <span className="bg-amber-500/10 text-amber-400 text-3xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md border border-amber-500/20">
                    {t("auto.profileoverlay.gratis_periode", "Gratis periode")}
                  </span>
                ) : (
                  <span className="bg-[var(--bg-surface-alt)] text-[var(--text-muted)] text-3xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md border border-[var(--border-primary)]">
                    {t('profile.membership_status.free_tier')}
                  </span>
                )}
              </div>

              {!isAdmin && user.paidUntil && Date.now() < user.paidUntil && user.subscriptionStatus !== 'paid' && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mt-2">
                  <p className="text-tiny text-amber-300 font-semibold leading-relaxed">
                    Du har gratis tilgang til alle premium-funksjoner til{' '}
                    <strong>{new Date(user.paidUntil).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
                    Tegn abonnement for å beholde tilgangen etter denne datoen.
                  </p>
                </div>
              )}

              {(user.subscriptionStatus === 'paid' || user.stripeCustomerId || user.vippsAgreementId) && (
                <button
                  type="button"
                  onClick={handleViewPaymentHistory}
                  disabled={isLoadingHistory}
                  className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center justify-between group transition-all hover:bg-[var(--bg-surface-alt)] mt-2"
                >
                  <div className="flex items-center gap-3">
                    <Receipt size={16} className="text-blue-400" />
                    <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('profile.membership_status.payment_history')}</span>
                  </div>
                  {isLoadingHistory ? (
                    <Loader2 size={14} className="text-[var(--text-muted)] animate-spin" />
                  ) : (
                    <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:translate-x-1 transition-transform" />
                  )}
                </button>
              )}

              {!isAdmin && user.subscriptionStatus !== 'paid' && !user.isTester && (
                <div className="pt-2 space-y-2">
                  <button
                    type="button"
                    onClick={handleStartSubscription}
                    disabled={isStartingSubscription}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg disabled:opacity-70"
                  >
                    {isStartingSubscription ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} fill="currentColor" />}
                    {t('profile.membership_status.start_subscription')}
                  </button>
                  <button
                    type="button"
                    onClick={onOpenUpgrade}
                    className="w-full bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-primary)] text-[var(--text-secondary)] py-2.5 rounded-xl text-3xs font-black uppercase tracking-widest"
                  >
                    {t('profile.membership_status.view_plans')}
                  </button>
                  {testerApplicationsEnabled ? (
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 space-y-3 mt-2">
                      <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-amber-400" />
                        <span className="text-2xs font-black text-amber-400 uppercase tracking-widest">
                          {t('profile.membership_status.tester_program_title')}
                        </span>
                      </div>
                      <p className="text-2xs text-[var(--text-secondary)] leading-relaxed">
                        {t('profile.membership_status.tester_program_desc')}
                      </p>
                      <button
                        type="button"
                        onClick={() => onOpenTesterApplication?.()}
                        className="w-full bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-400 py-2.5 rounded-xl text-3xs font-black uppercase tracking-widest transition-all"
                      >
                        {t('profile.membership_status.apply_tester')}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-stone-500/5 border border-stone-500/10 rounded-2xl p-3 flex items-center gap-2 mt-2">
                      <Sparkles size={14} className="text-[var(--text-muted)] shrink-0" />
                      <p className="text-2xs text-[var(--text-muted)] font-bold">{t('upgrade.tester.not_recruiting')}</p>
                    </div>
                  )}
                </div>
              )}

              {!isAdmin && !user.isTester && user.subscriptionStatus === 'paid' && !hasPremiumAccess(user) && (
                <button
                  type="button"
                  onClick={onOpenUpgrade}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg mt-2"
                >
                  <Car size={14} />
                  {t('profile.membership_status.upgrade_premium', 'Oppgrader til Premium')}
                </button>
              )}

              {user.subscriptionStatus === 'paid' && user.subscriptionProvider === 'apple' && (
                <div className="bg-stone-500/10 border border-stone-500/20 rounded-xl px-4 py-3 mt-2">
                  <p className="text-2xs text-[var(--text-secondary)] font-bold leading-relaxed">
                    {t('profile.membership_status.manage_apple', 'Abonnementet administreres via Apple. Gå til Innstillinger → Apple-ID → Abonnementer for å endre eller si opp.')}
                  </p>
                </div>
              )}

              {user.subscriptionStatus === 'paid' && user.subscriptionProvider === 'vipps' && (
                <button
                  type="button"
                  onClick={handleStopVipps}
                  disabled={isStoppingVipps}
                  className="w-full bg-[var(--bg-surface-alt)] border border-vipps/20 text-vipps py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-70 mt-2 hover:bg-vipps/5"
                >
                  {isStoppingVipps ? <Loader2 className="animate-spin" size={14} /> : <Wrench size={14} />}
                  {t('profile.membership_status.stop_vipps')}
                </button>
              )}

              {user.subscriptionStatus === 'paid' && user.subscriptionProvider !== 'vipps' && user.subscriptionProvider !== 'apple' && (
                <div className="mt-2 space-y-2">
                  <button
                    type="button"
                    onClick={handleManageBilling}
                    disabled={isOpeningBilling}
                    className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] text-[var(--text-secondary)] py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-70"
                  >
                    {isOpeningBilling ? <Loader2 className="animate-spin" size={14} /> : <Wrench size={14} />}
                    {t('profile.membership_status.manage_subscription')}
                  </button>
                  <p className="text-2xs text-[var(--text-muted)] text-center font-medium leading-relaxed">
                    {t('profile.membership_status.stripe_cancel_info')}
                  </p>
                </div>
              )}

              {user.isTester && (
                <>
                  <div className="bg-emerald-500/10 border border-emerald-500/20 p-3 rounded-xl mb-2">
                    <p className="text-2xs text-emerald-400 font-bold leading-relaxed">
                      {t('profile.membership_status.tester_notice')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleEndTester}
                    disabled={isEndingTester}
                    className="w-full bg-red-600/10 hover:bg-red-600/20 border border-red-500/30 text-red-300 py-3 rounded-xl text-2xs font-black uppercase tracking-widest disabled:opacity-60"
                  >
                    {isEndingTester ? t('common.loading') : t('profile.membership_status.end_tester')}
                  </button>
                </>
              )}

              {/* Admin uten testerrolle har ingen Ambassadør-fane — panelet må
                  finnes ett sted for dem også. */}
              {isAdmin && !user.isTester && (
                <div className="mt-4">
                  <BetaCountriesPanel user={user} />
                </div>
              )}

              <button
                onClick={() => window.open('/personvern', '_blank')}
                className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center justify-between group transition-all hover:bg-[var(--bg-surface-alt)] mt-4"
              >
                <div className="flex items-center gap-3">
                  <Info size={16} className="text-[var(--text-muted)]" />
                  <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('profile.account_actions.privacy_policy')}</span>
                </div>
                <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => window.open('/vilkar', '_blank')}
                className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center justify-between group transition-all hover:bg-[var(--bg-surface-alt)] mt-2"
              >
                <div className="flex items-center gap-3">
                  <Info size={16} className="text-[var(--text-muted)]" />
                  <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('profile.account_actions.terms_of_service', 'Brukervilkår')}</span>
                </div>
                <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Delete Account — also in Membership tab for Apple discoverability */}
              <div className="mt-6 pt-4 border-t border-[var(--border-primary)]">
                <button
                  type="button"
                  onClick={openDeleteFlow}
                  className="w-full bg-red-600/5 hover:bg-red-600/10 border border-red-600/20 text-red-400 py-3 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  <Trash2 size={14} /> {t('profile.account_actions.delete_account')}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'ambassador' && user.isTester && (
            <TesterStatusTab user={user} />
          )}

          {activeTab === 'garage' && (
            <div className="pb-8 animate-in slide-in-from-bottom-4 duration-500">
              <GarageTab user={user} />
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300 pb-12">
              {!isPaidUser ? (
                /* Premium upsell for free users */
                <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl p-6 text-center space-y-5">
                  <div className="flex justify-center">
                    <div className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-2xl">
                      <Lock size={24} className="text-violet-400" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-[13px] font-black text-[var(--text-primary)] uppercase tracking-widest">{t('profile.notifications.premium_title')}</h3>
                    <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('profile.notifications.premium_description')}</p>
                  </div>
                  <div className="space-y-2.5 text-left max-w-xs mx-auto">
                    {[
                      t('profile.notifications.premium_feature_push'),
                      t('profile.notifications.premium_feature_county'),
                      t('profile.notifications.premium_feature_spots'),
                      t('profile.notifications.premium_feature_reviews'),
                    ].map((feature) => (
                      <div key={feature} className="flex items-center gap-2.5">
                        <Check size={14} className="text-emerald-500 shrink-0" />
                        <span className="text-tiny text-[var(--text-secondary)] font-bold">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={onOpenUpgrade}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3.5 rounded-2xl font-black text-2xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 shadow-xl shadow-emerald-900/10"
                  >
                    <Zap size={14} />
                    {t('profile.notifications.premium_upgrade_btn')}
                  </button>
                </div>
              ) : (
                /* Paid user: show actual notification controls */
                <>
                  {/* Push notifications toggle */}
                  <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Bell size={16} className="text-violet-400" />
                      <div className="space-y-0.5">
                        <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest block">{t('profile.notifications.title')}</span>
                        <span className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">{effectiveNotifEnabled ? t('profile.notifications.enabled') : t('profile.notifications.disabled')}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={notifPending}
                      onClick={async () => {
                        const next = !effectiveNotifEnabled;
                        setOptimisticNotifEnabled(next);
                        setNotifPending(true);
                        try {
                          if (effectiveNotifEnabled) {
                            await disableNotifications(user.uid);
                            setNotifState(getNotificationPermissionState());
                          } else {
                            const granted = await requestNotificationPermission(user.uid);
                            if (!granted) {
                              setOptimisticNotifEnabled(false);
                            }
                            setNotifState(getNotificationPermissionState());
                          }
                        } catch {
                          setOptimisticNotifEnabled(null);
                        } finally {
                          setNotifPending(false);
                        }
                      }}
                      className={`w-11 h-6 rounded-full transition-all relative flex-shrink-0 disabled:opacity-60 ${effectiveNotifEnabled ? 'bg-emerald-600' : 'bg-[var(--bg-input)] border border-[var(--border-primary)]'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow-md ${effectiveNotifEnabled ? 'left-6' : 'left-1'}`}>
                        {notifPending && <Loader2 size={10} className="text-emerald-600 animate-spin absolute inset-0 m-auto" />}
                      </div>
                    </button>
                  </div>

                  {/* Bakgrunns-innsjekk (native OS-geofence) — kun når pluginen finnes */}
                  {GEOFENCE_CHECKIN_ENABLED && geofenceAvailable && (
                    <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MapPin size={16} className="text-violet-400" />
                        <div className="space-y-0.5">
                          <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest block">
                            {t('profile.geofence_checkin.title', 'Innsjekk-påminnelse på stedet')}
                          </span>
                          <span className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
                            {t('profile.geofence_checkin.description', 'Få spørsmål om å sjekke inn når du har vært en stund på en plass')}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={geofencePending}
                        onClick={async () => {
                          setGeofencePending(true);
                          try {
                            if (geofenceOn) {
                              try { localStorage.setItem(GEOFENCE_OPTOUT_KEY, '1'); } catch { /* ignore */ }
                              await GeofenceCheckIn.removeAllGeofences();
                              setGeofenceOn(false);
                            } else {
                              try { localStorage.removeItem(GEOFENCE_OPTOUT_KEY); } catch { /* ignore */ }
                              const res = await GeofenceCheckIn.requestPermissions();
                              setGeofenceOn(res.granted);
                            }
                          } catch { /* ignore */ } finally {
                            setGeofencePending(false);
                          }
                        }}
                        className={`w-11 h-6 rounded-full transition-all relative flex-shrink-0 disabled:opacity-60 ${geofenceOn ? 'bg-emerald-600' : 'bg-[var(--bg-input)] border border-[var(--border-primary)]'}`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow-md ${geofenceOn ? 'left-6' : 'left-1'}`}>
                          {geofencePending && <Loader2 size={10} className="text-emerald-600 animate-spin absolute inset-0 m-auto" />}
                        </div>
                      </button>
                    </div>
                  )}

                  {/* County Notifications */}
                  <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-[var(--border-primary)]">
                      <div className="flex items-center gap-3">
                        <MapPin size={16} className="text-emerald-500" />
                        <div className="space-y-0.5 flex-1">
                          <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest block">{t('profile.county_notifications.title', 'Mine interesseområder')}</span>
                          <span className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">{t('profile.county_notifications.description', 'Få varsler fra valgte fylker')}</span>
                        </div>
                        {countySaveStatus === 'saving' && <Loader2 size={14} className="text-emerald-400 animate-spin flex-shrink-0" />}
                        {countySaveStatus === 'saved' && <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0 animate-in fade-in duration-200" />}
                        {countySaveStatus === 'error' && <AlertTriangle size={14} className="text-red-400 flex-shrink-0 animate-in fade-in duration-200" />}
                      </div>
                    </div>
                    <div className="divide-y divide-[var(--border-primary)]">
                      {NORWEGIAN_COUNTIES.map(county => {
                        const enabled = countyNotifSpots.includes(county) || countyNotifReviews.includes(county);
                        return (
                          <div key={county}>
                            <div className="px-4 py-2.5 flex items-center justify-between">
                              <span className="text-2xs font-bold text-[var(--text-secondary)] uppercase tracking-wider flex-1">{county}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  let nextSpots: string[], nextReviews: string[];
                                  if (enabled) {
                                    nextSpots = countyNotifSpots.filter(c => c !== county);
                                    nextReviews = countyNotifReviews.filter(c => c !== county);
                                  } else {
                                    nextSpots = [...countyNotifSpots, county];
                                    nextReviews = [...countyNotifReviews, county];
                                  }
                                  setCountyNotifSpots(nextSpots);
                                  setCountyNotifReviews(nextReviews);
                                  saveCountyPrefs(nextSpots, nextReviews);
                                }}
                                className={`w-11 h-6 rounded-full transition-all relative flex-shrink-0 ${enabled ? 'bg-emerald-600' : 'bg-[var(--bg-input)] border border-[var(--border-primary)]'}`}
                              >
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow-md ${enabled ? 'left-6' : 'left-1'}`} />
                              </button>
                            </div>
                            {enabled && (
                              <div className="px-4 pb-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                                <div className="flex items-center justify-between pl-4">
                                  <span className="text-3xs text-[var(--text-secondary)] font-bold uppercase tracking-widest">{t('profile.county_notifications.new_spots', 'Nye bekreftede plasser')}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const has = countyNotifSpots.includes(county);
                                      const nextSpots = has
                                        ? countyNotifSpots.filter(c => c !== county)
                                        : [...countyNotifSpots, county];
                                      setCountyNotifSpots(nextSpots);
                                      saveCountyPrefs(nextSpots, countyNotifReviews);
                                    }}
                                    className={`w-9 h-5 rounded-full transition-all relative flex-shrink-0 ${countyNotifSpots.includes(county) ? 'bg-emerald-600' : 'bg-[var(--bg-input)] border border-[var(--border-primary)]'}`}
                                  >
                                    <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all shadow-md ${countyNotifSpots.includes(county) ? 'left-[18px]' : 'left-0.5'}`} />
                                  </button>
                                </div>
                                <div className="flex items-center justify-between pl-4">
                                  <span className="text-3xs text-[var(--text-secondary)] font-bold uppercase tracking-widest">{t('profile.county_notifications.new_reviews', 'Nye anmeldelser')}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const has = countyNotifReviews.includes(county);
                                      const nextReviews = has
                                        ? countyNotifReviews.filter(c => c !== county)
                                        : [...countyNotifReviews, county];
                                      setCountyNotifReviews(nextReviews);
                                      saveCountyPrefs(countyNotifSpots, nextReviews);
                                    }}
                                    className={`w-9 h-5 rounded-full transition-all relative flex-shrink-0 ${countyNotifReviews.includes(county) ? 'bg-emerald-600' : 'bg-[var(--bg-input)] border border-[var(--border-primary)]'}`}
                                  >
                                    <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all shadow-md ${countyNotifReviews.includes(county) ? 'left-[18px]' : 'left-0.5'}`} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Inbox messages from admin */}
                  {inboxMessages.length > 0 && (
                    <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-[var(--border-primary)] flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Mail size={16} className="text-sky-400" />
                          <div className="space-y-0.5">
                            <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest block">{t("auto.profileoverlay.meldinger_fra_fricamp", "Meldinger fra Fricamp")}</span>
                            {(inboxMeta?.unreadCount ?? 0) > 0 && (
                              <span className="text-3xs text-sky-400 font-bold uppercase tracking-widest">{inboxMeta!.unreadCount} ulest</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="divide-y divide-[var(--border-primary)]">
                        {inboxMessages.slice(0, 10).map(msg => (
                          <div
                            key={msg.id}
                            className={`px-4 py-3 cursor-pointer transition-colors hover:bg-white/3 ${!msg.isRead ? 'bg-sky-500/5' : ''}`}
                            onClick={() => {
                              if (openMessageId === msg.id) {
                                setOpenMessageId(null);
                              } else {
                                setOpenMessageId(msg.id);
                                if (!msg.isRead) markMessageAsRead(user.uid, msg.id);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {!msg.isRead ? <Mail size={12} className="text-sky-400 flex-shrink-0" /> : <MailOpen size={12} className="text-[var(--text-muted)] flex-shrink-0" />}
                              <span className={`text-tiny font-bold flex-1 truncate ${!msg.isRead ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>{msg.subject}</span>
                              <ChevronRight size={12} className={`text-[var(--text-muted)] flex-shrink-0 transition-transform ${openMessageId === msg.id ? 'rotate-90' : ''}`} />
                            </div>
                            {openMessageId === msg.id && (
                              <div className="mt-2 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                                <p className="text-tiny text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{msg.body}</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
                                    {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' }) : ''}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setShowTesterInbox(true); }}
                                    className="flex items-center gap-1.5 text-3xs font-black text-emerald-400 hover:text-emerald-300 uppercase tracking-widest transition-colors"
                                  >
                                    <Send size={10} />
                                    {t("auto.profileoverlay.svar_til_fricamp", "Svar til Fricamp")}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contact / feedback button (always visible) */}
                  <button
                    onClick={() => setShowTesterInbox(true)}
                    className="w-full bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-2xl px-4 py-3 flex items-center gap-3 transition-colors"
                  >
                    <MessageSquare size={16} className="text-emerald-400" />
                    <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">{t("auto.profileoverlay.kontakt_fricamp", "Kontakt Fricamp")}</span>
                    <ChevronRight size={14} className="text-[var(--text-muted)] ml-auto" />
                  </button>

                </>
              )}
            </div>
          )}

          {activeTab === 'visits' && (
            <div className="pb-8 animate-in slide-in-from-bottom-4 duration-500">
              <VisitsTab
                visits={visits}
                onSpotClick={onFlyToSpot ? (spotId, lat, lng) => { onFlyToSpot(spotId, lat, lng); onClose(); } : undefined}
                userPhotoURL={user.photoURL}
                onCloseOverlay={onClose}
                spots={spots}
                user={user}
                onVisitsEnriched={setVisits}
              />
            </div>
          )}

        </div>
      </div>

      {offboarding && (
        <OffboardingSurveyModal
          user={user}
          type={offboarding.type}
          provider={offboarding.provider}
          onComplete={offboarding.onContinue}
          onDismiss={() => setOffboarding(null)}
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed left-0 right-0 bottom-0 z-[10003] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => { if (!isDeleting) setShowDeleteConfirm(false); }}
          />
          <div className="relative w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-3xl shadow-3xl overflow-hidden animate-in zoom-in-95 fade-in duration-200">
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="text-lg font-black text-[var(--text-primary)] tracking-tight">{t('profile.account_actions.delete_confirm_title')}</h3>
                  <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">
                    {t('profile.account_actions.delete_confirm_text')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-60"
                  aria-label={t('common.close')}
                >
                  <X size={20} />
                </button>
              </div>

              {deleteError && (
                <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-2xl">
                  <p className="text-tiny text-red-200 font-bold">{deleteError}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="flex-1 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] text-[var(--text-primary)] py-3 rounded-2xl text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-60"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-500 text-white py-3 rounded-2xl text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {isDeleting ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
                  {t('common.delete')}
                </button>
              </div>

              <p className="text-2xs text-[var(--text-muted)]">
                {t('profile.account_actions.delete_tip')}
              </p>
            </div>
          </div>
        </div>
      )}


      {showPaymentHistory && (
        <div className="fixed left-0 right-0 bottom-0 z-[10003] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowPaymentHistory(false)} />
          <div className="relative w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-200 max-h-[80vh] flex flex-col">
            <div className="p-5 pb-3 flex items-center justify-between border-b border-[var(--border-primary)]">
              <h3 className="text-sm font-black text-[var(--text-primary)] tracking-tight">{t('profile.membership_status.payment_history')}</h3>
              <button onClick={() => setShowPaymentHistory(false)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 pt-3" style={{ overscrollBehavior: 'contain' }}>
              {isLoadingHistory ? (
                <div className="flex justify-center py-12">
                  <Loader2 size={24} className="animate-spin text-blue-400" />
                </div>
              ) : paymentHistory.length === 0 ? (
                <p className="text-center text-[var(--text-muted)] text-tiny font-bold uppercase tracking-widest py-12">
                  {t('profile.membership_status.no_transactions')}
                </p>
              ) : (
                <div className="space-y-2">
                  {paymentHistory.map((tx) => (
                    <div key={tx.id} className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-xl px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <p className="text-tiny font-bold text-[var(--text-primary)]">{tx.description}</p>
                          <p className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
                            {new Date(tx.date).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })}
                            {' · '}
                            <span className={tx.provider === 'vipps' ? 'text-vipps' : 'text-indigo-400'}>
                              {tx.provider === 'vipps' ? 'Vipps' : 'Stripe'}
                            </span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[12px] font-black text-emerald-400">{tx.amount.toFixed(0)} {tx.currency}</p>
                          <p className="text-4xs font-black uppercase tracking-widest text-emerald-600">
                            {tx.status === 'paid' ? t('profile.membership_status.tx_paid') : t('profile.membership_status.tx_pending')}
                          </p>
                        </div>
                      </div>
                      {tx.receiptUrl && (
                        <button
                          onClick={() => window.open(tx.receiptUrl!, '_blank')}
                          className="mt-2 text-3xs font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1 uppercase tracking-widest"
                        >
                          <ExternalLink size={10} />
                          {t('profile.membership_status.view_receipt')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showTesterInbox && (
        <TesterInboxModal
          currentUser={user}
          isAdmin={false}
          onClose={() => setShowTesterInbox(false)}
        />
      )}
    </div>
  );
};

export default ProfileOverlay;
